import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Card from './components/Card';
import Loader from './components/Loader';
import Button from './components/Button';
import Badge from './components/Badge';
import { getInternalLinks, getDuplicateContent, checkBrokenLinks } from './api';
import { useToast } from './ToastContext';
import {
  Link2, AlertTriangle, CheckCircle2, ArrowRight, Search,
  Copy, ExternalLink, ChevronDown, ChevronRight, RefreshCw
} from 'lucide-react';

function StatCard({ label, value, color = 'blue', sub }) {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    red:    'bg-red-50 border-red-200 text-red-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    gray:   'bg-blue-gray-50 border-blue-gray-200 text-blue-gray-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm mt-0.5">{label}</div>
      {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function PageRow({ page, slug }) {
  const [open, setOpen] = useState(false);
  const scoreColor = page.score_global >= 90 ? 'green' : page.score_global >= 60 ? 'warning' : 'red';
  return (
    <div className="border border-blue-gray-200 rounded-lg hover:border-accent/40 transition-colors">
      <div
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {page.is_orphan && (
              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium shrink-0">Orpheline</span>
            )}
            <span className="text-sm font-medium text-blue-gray-900 truncate">
              {page.title || page.url}
            </span>
          </div>
          <div className="text-xs text-blue-gray-400 truncate font-mono mt-0.5">{page.url}</div>
        </div>
        <div className="flex items-center gap-4 shrink-0 text-sm">
          <div className="text-center">
            <div className="font-bold text-blue-gray-800">{page.inlinks_count}</div>
            <div className="text-xs text-blue-gray-400">entrants</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-blue-gray-800">{page.outlinks_count}</div>
            <div className="text-xs text-blue-gray-400">sortants</div>
          </div>
          <Badge color={scoreColor} size="sm">{Math.round(page.score_global)}/100</Badge>
          <a
            href={page.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="text-blue-gray-400 hover:text-accent transition-colors"
          >
            <ExternalLink size={14} />
          </a>
          {page.outlinks.length > 0 ? (
            open ? <ChevronDown size={14} className="text-blue-gray-400" /> : <ChevronRight size={14} className="text-blue-gray-400" />
          ) : <span className="w-3.5" />}
        </div>
      </div>
      {open && page.outlinks.length > 0 && (
        <div className="border-t border-blue-gray-100 px-4 pb-3 pt-2">
          <div className="text-xs font-semibold text-blue-gray-500 mb-2">Liens sortants ({page.outlinks.length}{page.outlinks.length === 50 ? '+' : ''})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {page.outlinks.map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <ArrowRight size={12} className="text-blue-gray-300 shrink-0" />
                {l.nofollow && <span className="text-amber-600 font-mono">nofollow</span>}
                <a href={l.href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline truncate font-mono">{l.href}</a>
                {l.anchor && <span className="text-blue-gray-400 shrink-0">"{l.anchor}"</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectInternalLinks() {
  const { slug } = useParams();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dupData, setDupData] = useState(null);
  const [brokenData, setBrokenData] = useState(null);
  const [brokenLoading, setBrokenLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('pages'); // pages | orphans | duplicates | broken

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [links, dups] = await Promise.all([
          getInternalLinks(slug),
          getDuplicateContent(slug),
        ]);
        setData(links);
        setDupData(dups);
      } catch (e) {
        showToast(e.response?.data?.detail || 'Erreur lors du chargement', 'error');
      } finally {
        setLoading(false);
      }
    }
    if (slug) load();
  }, [slug]);

  const handleCheckBroken = async () => {
    if (brokenLoading) return;
    setBrokenLoading(true);
    try {
      const result = await checkBrokenLinks(slug, 150);
      setBrokenData(result);
      setTab('broken');
      showToast(`Vérification terminée : ${result.broken_count} lien(s) brisé(s) sur ${result.checked} vérifié(s)`, result.broken_count > 0 ? 'warning' : 'success');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Erreur lors de la vérification', 'error');
    } finally {
      setBrokenLoading(false);
    }
  };

  const tabs = [
    { key: 'pages', label: 'Toutes les pages' },
    { key: 'orphans', label: `Pages orphelines${data ? ` (${data.orphan_pages?.length ?? 0})` : ''}` },
    { key: 'duplicates', label: `Doublons${dupData ? ` (${dupData.total_issues})` : ''}` },
    { key: 'broken', label: `Liens brisés${brokenData ? ` (${brokenData.broken_count})` : ''}` },
  ];

  const filteredPages = (data?.pages || []).filter(p =>
    !search || p.url.toLowerCase().includes(search.toLowerCase()) || (p.title || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center py-24"><Loader /></div>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-blue-gray-900 flex items-center gap-2">
            <Link2 size={22} className="text-accent" /> Maillage interne
          </h1>
          <p className="text-sm text-blue-gray-500 mt-1">Analyse des liens internes, pages orphelines et contenus dupliqués</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCheckBroken}
          disabled={brokenLoading}
          className="flex items-center gap-2 shrink-0"
        >
          <RefreshCw size={14} className={brokenLoading ? 'animate-spin' : ''} />
          {brokenLoading ? 'Vérification...' : 'Vérifier les liens brisés'}
        </Button>
      </div>

      {/* Alertes */}
      {data?.warnings?.length > 0 && (
        <div className="flex flex-col gap-2">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-800">
              <AlertTriangle size={15} className="shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Pages analysées" value={data.total_pages} color="blue" />
          <StatCard label="Liens internes totaux" value={data.total_internal_links} color="blue" />
          <StatCard label="Liens sortants moy./page" value={data.avg_outlinks} color={data.avg_outlinks >= 2 ? 'green' : 'amber'} />
          <StatCard label="Pages orphelines" value={data.orphan_pages?.length ?? 0} color={data.orphan_pages?.length > 0 ? 'red' : 'green'} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-blue-gray-100 rounded-xl p-1 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'bg-white text-blue-gray-900 shadow-sm'
                : 'text-blue-gray-500 hover:text-blue-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'pages' && (
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-gray-400" />
              <input
                type="text"
                placeholder="Filtrer par URL ou titre..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-blue-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
              />
            </div>
            <span className="text-sm text-blue-gray-400 shrink-0">{filteredPages.length} page(s)</span>
          </div>
          <div className="space-y-2">
            {filteredPages.map(page => (
              <PageRow key={page.url} page={page} slug={slug} />
            ))}
            {filteredPages.length === 0 && (
              <div className="text-center py-8 text-blue-gray-400 text-sm">Aucune page trouvée</div>
            )}
          </div>
        </Card>
      )}

      {tab === 'orphans' && (
        <Card>
          <h2 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">
            Pages orphelines ({data?.orphan_pages?.length ?? 0})
          </h2>
          {data?.orphan_pages?.length > 0 ? (
            <div className="space-y-2">
              {data.orphan_pages.map(page => (
                <PageRow key={page.url} page={page} slug={slug} />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-3 text-sm">
              <CheckCircle2 size={16} /> Aucune page orpheline détectée !
            </div>
          )}
        </Card>
      )}

      {tab === 'duplicates' && dupData && (
        <div className="flex flex-col gap-4">
          <Card>
            <h2 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">
              Titles dupliqués ({dupData.duplicate_titles.length})
            </h2>
            {dupData.duplicate_titles.length > 0 ? (
              <div className="space-y-3">
                {dupData.duplicate_titles.map((d, i) => (
                  <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="text-sm font-medium text-red-800 mb-2">"{d.value}"</div>
                    <div className="space-y-1">
                      {d.pages.map((p, j) => (
                        <a key={j} href={p.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-accent hover:underline font-mono truncate">
                          <ExternalLink size={11} /> {p.url}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-3 text-sm">
                <CheckCircle2 size={16} /> Aucun title dupliqué !
              </div>
            )}
          </Card>
          <Card>
            <h2 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">
              Meta descriptions dupliquées ({dupData.duplicate_metas.length})
            </h2>
            {dupData.duplicate_metas.length > 0 ? (
              <div className="space-y-3">
                {dupData.duplicate_metas.map((d, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="text-sm font-medium text-amber-800 mb-2 italic">"{d.value}"</div>
                    <div className="space-y-1">
                      {d.pages.map((p, j) => (
                        <a key={j} href={p.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-accent hover:underline font-mono truncate">
                          <ExternalLink size={11} /> {p.url}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-3 text-sm">
                <CheckCircle2 size={16} /> Aucune meta description dupliquée !
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'broken' && (
        <Card>
          {brokenData ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-base font-semibold text-blue-gray-900">
                  Liens brisés ({brokenData.broken_count} / {brokenData.checked} vérifiés)
                </h2>
                <Button variant="secondary" size="sm" onClick={handleCheckBroken} disabled={brokenLoading}
                  className="flex items-center gap-2 text-xs">
                  <RefreshCw size={12} className={brokenLoading ? 'animate-spin' : ''} />
                  Relancer
                </Button>
              </div>
              {brokenData.broken_links.length > 0 ? (
                <div className="space-y-2">
                  {brokenData.broken_links.map((l, i) => (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${l.status === 0 ? 'bg-gray-200 text-gray-700' : 'bg-red-200 text-red-800'}`}>
                          {l.status === 0 ? 'Timeout' : l.status}
                        </span>
                        <a href={l.href} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-mono text-accent hover:underline truncate">
                          {l.href}
                        </a>
                      </div>
                      {l.found_on?.length > 0 && (
                        <div className="text-xs text-red-600">
                          Trouvé sur : {l.found_on.slice(0, 3).join(', ')}
                          {l.found_on.length > 3 && ` +${l.found_on.length - 3}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-3 text-sm">
                  <CheckCircle2 size={16} /> Aucun lien brisé détecté parmi les {brokenData.checked} liens vérifiés !
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-blue-gray-400">
              <Link2 size={32} className="mx-auto mb-3 opacity-30" />
              <div className="text-sm mb-4">Cliquez sur "Vérifier les liens brisés" pour lancer l'analyse</div>
              <Button variant="secondary" onClick={handleCheckBroken} disabled={brokenLoading}
                className="flex items-center gap-2 mx-auto">
                <RefreshCw size={14} className={brokenLoading ? 'animate-spin' : ''} />
                {brokenLoading ? 'Vérification en cours...' : 'Vérifier maintenant'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Tableau des pages les plus liées */}
      {tab === 'pages' && data?.most_linked?.length > 0 && (
        <Card>
          <h2 className="font-heading text-base font-semibold text-blue-gray-900 mb-4">
            Pages les plus liées (top 10)
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-gray-100 text-left text-xs text-blue-gray-500">
                  <th className="pb-2 pr-4">#</th>
                  <th className="pb-2 pr-4">Page</th>
                  <th className="pb-2 pr-4 text-right">Entrants</th>
                  <th className="pb-2 text-right">Sortants</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-gray-50">
                {data.most_linked.map((page, i) => (
                  <tr key={page.url}>
                    <td className="py-2 pr-4 text-blue-gray-400 font-mono">{i + 1}</td>
                    <td className="py-2 pr-4">
                      <div className="font-medium text-blue-gray-900 truncate max-w-xs">{page.title || page.url}</div>
                      <div className="text-xs text-blue-gray-400 font-mono truncate">{page.url}</div>
                    </td>
                    <td className="py-2 pr-4 text-right font-bold text-blue-700">{page.inlinks_count}</td>
                    <td className="py-2 text-right text-blue-gray-600">{page.outlinks_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
